import React, { useMemo } from 'react';
import {
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  InputAdornment,
  makeStyles,
  Theme,
} from '@bb-ui/react-library';
import { IconButton } from '@bb-ui/react-library/dist/components/IconButton';
import { Search } from '@bb-ui/icons/dist/small/Search';
import { Clear } from '@bb-ui/icons/dist/small/Clear';
import { useTranslation } from 'react-i18next';
import { FAILED_SITE_STATUSES } from '../../utils';

const ALL_QUICK_SELECT = '__ALL_QUICK_SELECT__';
const FAILED_QUICK_SELECT = '__FAILED_QUICK_SELECT__';
const STATUS_ITEM_HEIGHT = 48;

interface SitesTableToolbarProps {
  searchText: string;
  onSearchChange: (value: string) => void;
  statusFilter: string[];
  onStatusFilterChange: (value: string[]) => void;
  availableStatuses: string[];
  disabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  search: {
    flex: 1,
  },
  statusFilter: {
    width: 220,
    flexShrink: 0,
  },
  metaDivider: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const SitesTableToolbar: React.FC<SitesTableToolbarProps> = ({
  searchText,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  availableStatuses,
  disabled = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const failedAvailable = useMemo(
    () => FAILED_SITE_STATUSES.filter((status) => availableStatuses.includes(status)),
    [availableStatuses],
  );

  const orderedStatuses = useMemo(
    () => [...failedAvailable, ...availableStatuses.filter((s) => !failedAvailable.includes(s))],
    [availableStatuses, failedAvailable],
  );

  const allSelected =
    availableStatuses.length > 0 &&
    availableStatuses.every((status) => statusFilter.includes(status));

  const allFailedSelected =
    failedAvailable.length > 0 && failedAvailable.every((status) => statusFilter.includes(status));
  const someFailedSelected = failedAvailable.some((status) => statusFilter.includes(status));

  const metaItemCount = failedAvailable.length > 0 ? 2 : 1;

  const handleStatusChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target;
    const raw = typeof value === 'string' ? value.split(',') : (value as string[]);

    if (raw.includes(ALL_QUICK_SELECT)) {
      onStatusFilterChange(allSelected ? [] : [...availableStatuses]);
      return;
    }

    if (raw.includes(FAILED_QUICK_SELECT)) {
      const cleaned = raw.filter((status) => status !== FAILED_QUICK_SELECT);
      if (allFailedSelected) {
        onStatusFilterChange(cleaned.filter((status) => !failedAvailable.includes(status)));
      } else {
        onStatusFilterChange(Array.from(new Set([...cleaned, ...failedAvailable])));
      }
      return;
    }

    onStatusFilterChange(raw);
  };

  const renderStatusValue = (selected: string[]) => {
    if (!selected.length || selected.length === availableStatuses.length) {
      return t('cohorts.allStatuses');
    }
    if (selected.length === 1) return selected[0];
    return t('cohorts.statusesSelected', { count: selected.length });
  };

  return (
    <div className={classes.toolbar}>
      <TextField
        id="sites-table-search"
        floatingLabel
        label={t('cohorts.searchSites')}
        placeholder={t('cohorts.searchSitesPlaceholder')}
        className={classes.search}
        value={searchText}
        onChange={(e) => onSearchChange(e.target.value)}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
          endAdornment: searchText ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label={t('cohorts.clearSearch')}
                onClick={() => onSearchChange('')}
                disabled={disabled}
              >
                <Clear />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />
      <TextField
        id="sites-table-status-filter"
        floatingLabel
        select
        label={t('cohorts.filterByStatus')}
        className={classes.statusFilter}
        value={statusFilter}
        onChange={handleStatusChange}
        disabled={disabled || availableStatuses.length === 0}
        InputLabelProps={{ shrink: true }}
        SelectProps={{
          multiple: true,
          displayEmpty: true,
          renderValue: (selected) => renderStatusValue(selected as string[]),
          MenuProps: {
            variant: 'menu',
            PaperProps: {
              style: {
                maxHeight: (metaItemCount + 4) * STATUS_ITEM_HEIGHT,
              },
            },
          },
        }}
      >
        <MenuItem value={ALL_QUICK_SELECT} className={classes.metaDivider}>
          <Checkbox checked={allSelected} onChange={() => {}} />
          <ListItemText primary={t('cohorts.allStatuses')} />
        </MenuItem>
        {failedAvailable.length > 0 && (
          <MenuItem value={FAILED_QUICK_SELECT}>
            <Checkbox
              checked={allFailedSelected}
              indeterminate={!allFailedSelected && someFailedSelected}
              onChange={() => {}}
            />
            <ListItemText primary={t('cohorts.failedStatuses')} />
          </MenuItem>
        )}
        {orderedStatuses.map((status, index) => (
          <MenuItem
            key={status}
            value={status}
            className={
              failedAvailable.length > 0 && index === failedAvailable.length - 1
                ? classes.metaDivider
                : undefined
            }
          >
            <Checkbox checked={statusFilter.includes(status)} onChange={() => {}} />
            <ListItemText primary={status} />
          </MenuItem>
        ))}
      </TextField>
    </div>
  );
};

export default SitesTableToolbar;
